/**
 * The shared engine: resolves the district's mappings by precedence,
 * applies a fiscal year's declarative rules to built records, fingerprints
 * records so a change at the source is noticed, and compares two sets of
 * records element by element. It knows nothing about any one state.
 */

import type { FiscalYearPackage, Finding, IStateReportingProvider, Lineage, MappingResolver, MappingType, ProviderContext, StateCodeMapping, StateRecord, ValidationRule } from './types.js'

// ─────────────────────────────────────────────────────────────────────────────
// Mappings
// ─────────────────────────────────────────────────────────────────────────────

const SCOPE_RANK = { course: 3, school: 2, district: 1 } as const

/**
 * Course-specific beats school beats district; within a level, the one in
 * effect on the date wins. Two active mappings at the same level and date
 * that disagree are ambiguous — reported, never silently picked.
 */
export function mappingResolver(mappings: StateCodeMapping[], fiscalYear: string): MappingResolver & { ambiguities: { type: MappingType; localValue: string; values: string[] }[] } {
  const active = mappings.filter((m) => m.status === 'active' && m.fiscalYear === fiscalYear)
  const byKey = new Map<string, StateCodeMapping[]>()
  for (const m of active) {
    const k = `${m.mappingType}|${m.localValue.trim().toLowerCase()}`
    byKey.set(k, [...(byKey.get(k) ?? []), m])
  }
  const ambiguities: { type: MappingType; localValue: string; values: string[] }[] = []
  for (const [k, ms] of byKey) {
    const groups = new Map<string, Set<string>>()
    for (const m of ms) {
      const g = `${m.scope.level}|${m.scope.schoolId ?? ''}|${m.scope.courseId ?? ''}|${m.effectiveStart ?? ''}`
      groups.set(g, new Set([...(groups.get(g) ?? []), m.stateValue]))
    }
    for (const vals of groups.values()) if (vals.size > 1) ambiguities.push({ type: k.split('|')[0] as MappingType, localValue: ms[0]!.localValue, values: [...vals] })
  }
  return {
    ambiguities,
    resolve(type, localValue, at) {
      if (localValue == null || localValue === '') return null
      const ms = byKey.get(`${type}|${String(localValue).trim().toLowerCase()}`) ?? []
      const date = at.date ?? null
      const inEffect = ms.filter((m) => (!date || ((!m.effectiveStart || m.effectiveStart <= date) && (!m.effectiveEnd || m.effectiveEnd >= date))))
      const fits = inEffect.filter((m) => m.scope.level === 'district' || (m.scope.level === 'school' && m.scope.schoolId === at.schoolId) || (m.scope.level === 'course' && m.scope.courseId === at.courseId))
      const best = fits.sort((a, b) => SCOPE_RANK[b.scope.level] - SCOPE_RANK[a.scope.level] || (b.effectiveStart ?? '').localeCompare(a.effectiveStart ?? ''))[0]
      return best ? { stateValue: best.stateValue, mapping: best } : null
    },
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────────

export const rulesFor = (fy: FiscalYearPackage, recordCodes: string[], on?: string) =>
  fy.rules.filter((r) => recordCodes.includes(r.recordCode) && (!on || ((!r.effectiveStart || r.effectiveStart <= on) && (!r.effectiveEnd || r.effectiveEnd >= on))))

/** A rule set's identity, kept on every run and submission. */
export const ruleSetVersion = (fy: FiscalYearPackage, rules: ValidationRule[]) => `${fy.code}/${fy.schemaVersion}/${hashText(rules.map((r) => `${r.id}@${r.version}`).sort().join(','))}`

function finding(rule: ValidationRule, rec: StateRecord, el: string | null, elName: string | null, value: string | null, message: string, expected: string, lineage: Lineage | undefined, kind: Finding['kind'], fallbackDomain: Finding['domain']): Finding {
  const mapping = lineage?.mapping
  return {
    ruleId: rule.id, ruleVersion: rule.version, severity: rule.severity, kind, recordCode: rec.recordCode, recordKey: rec.key, entityType: rec.entityType, entityId: rec.entityId, entityName: rec.entityName, schoolId: rec.schoolId,
    elementCode: el, elementName: elName, currentValue: value, expected, message, domain: lineage?.domain ?? fallbackDomain,
    fix: kind === 'mapping' && mapping ? { label: 'Map in Code Mappings', to: `/state-reporting/mappings?type=${mapping.type}&value=${encodeURIComponent(mapping.localValue)}` } : lineage?.fix ?? null,
    mappingType: mapping?.type ?? null, localValue: mapping?.localValue ?? null,
  }
}

/**
 * The validation engine. Declarative rules run here for every state; a
 * provider supplies only its named cross-record checks. Retired elements are
 * never validated.
 */
export function validateRecords(provider: IStateReportingProvider, pctx: ProviderContext, records: StateRecord[]): Finding[] {
  const { fy, collection } = pctx
  const rules = rulesFor(fy, collection.recordCodes, collection.closeAt)
  const defs = new Map(fy.records.map((r) => [r.code, r]))
  const codeSets = new Map(fy.codeSets.map((c) => [c.id, new Set(c.values.map((v) => v.code))]))
  const out: Finding[] = []
  const byRecord = new Map<string, StateRecord[]>()
  for (const r of records) byRecord.set(r.recordCode, [...(byRecord.get(r.recordCode) ?? []), r])
  for (const rule of rules) {
    const def = defs.get(rule.recordCode)
    if (!def || def.status === 'retired') continue
    if (rule.logic.kind === 'custom') {
      const check = provider.customChecks[rule.logic.check]
      if (check) out.push(...check(pctx, byRecord.get(rule.recordCode) ?? [], rule))
      continue
    }
    const els = rule.elements.map((c) => def.elements.find((e) => e.code === c)).filter((e) => e && !e.retired) as NonNullable<(typeof def.elements)[number]>[]
    if (!els.length) continue
    for (const rec of byRecord.get(rule.recordCode) ?? []) {
      for (const el of els) {
        const v = rec.fields[el.code] ?? ''
        const lin = rec.lineage[el.code]
        const logic = rule.logic
        if (logic.kind === 'required') {
          if (v.trim()) continue
          // A local value with no state value is a mapping problem; no value at all is a source problem.
          const unmapped = Boolean(lin?.mapping && lin.mapping.localValue && !lin.mapping.mappingId)
          out.push(finding(rule, rec, el.code, el.name, unmapped ? lin!.mapping!.localValue : null, unmapped ? `${el.name}: “${lin!.mapping!.localValue}” has no state code mapped` : `${el.name} is missing`, rule.description, lin, unmapped ? 'mapping' : 'source', def.domain))
        } else if (logic.kind === 'code_set') {
          if (!v.trim() || !el.codeSet) continue
          if (!codeSets.get(el.codeSet)?.has(v)) out.push(finding(rule, rec, el.code, el.name, v, `${el.name} “${v}” is not a valid ${fy.code} code`, rule.description, lin, lin?.mapping ? 'mapping' : 'source', def.domain))
        } else if (logic.kind === 'format') {
          if (!v.trim()) continue
          if (!new RegExp(logic.pattern).test(v)) out.push(finding(rule, rec, el.code, el.name, el.sensitive ? maskId(v) : v, `${el.name} is not in the expected format (e.g. ${logic.example})`, rule.description, lin, 'source', def.domain))
        } else if (logic.kind === 'date_order') {
          const a = rec.fields[logic.before], b = rec.fields[logic.after]
          if (a && b && a > b) out.push(finding(rule, rec, logic.after, def.elements.find((e) => e.code === logic.after)?.name ?? logic.after, b, `${def.elements.find((e) => e.code === logic.after)?.name ?? logic.after} (${b}) is before ${def.elements.find((e) => e.code === logic.before)?.name ?? logic.before} (${a})`, rule.description, rec.lineage[logic.after], 'source', def.domain))
          break
        } else if (logic.kind === 'date_in_fiscal_year') {
          if (v && (v < fy.effectiveStart || v > fy.effectiveEnd)) out.push(finding(rule, rec, el.code, el.name, v, `${el.name} ${v} is outside ${fy.label}`, rule.description, lin, 'source', def.domain))
        }
      }
    }
  }
  return out
}

export const maskId = (v: string) => (v.length <= 4 ? '••••' : `${'•'.repeat(Math.max(0, v.length - 3))}${v.slice(-3)}`)

// ─────────────────────────────────────────────────────────────────────────────
// Fingerprints and differences
// ─────────────────────────────────────────────────────────────────────────────

/** FNV-1a, enough to notice that a record's reported values changed. Files are hashed with SHA-256 on the server. */
export function hashText(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return h.toString(16).padStart(8, '0')
}
export const recordHash = (r: Pick<StateRecord, 'recordCode' | 'key' | 'fields'>) => hashText(`${r.recordCode}|${r.key}|${Object.keys(r.fields).sort().map((k) => `${k}=${r.fields[k]}`).join('|')}`)
export const recordId = (r: Pick<StateRecord, 'recordCode' | 'key'>) => `${r.recordCode}:${r.key}`

export interface RecordDiff {
  added: { id: string; recordCode: string; entityName: string }[]
  removed: { id: string; recordCode: string; entityName: string }[]
  changed: { id: string; recordCode: string; entityName: string; elements: { code: string; before: string; after: string }[] }[]
  unchanged: number
}

export function diffRecords(before: StateRecord[], after: StateRecord[]): RecordDiff {
  const a = new Map(before.map((r) => [recordId(r), r]))
  const b = new Map(after.map((r) => [recordId(r), r]))
  const out: RecordDiff = { added: [], removed: [], changed: [], unchanged: 0 }
  for (const [id, r] of b) {
    const old = a.get(id)
    if (!old) { out.added.push({ id, recordCode: r.recordCode, entityName: r.entityName }); continue }
    const els = [...new Set([...Object.keys(old.fields), ...Object.keys(r.fields)])].filter((k) => (old.fields[k] ?? '') !== (r.fields[k] ?? '')).map((k) => ({ code: k, before: old.fields[k] ?? '', after: r.fields[k] ?? '' }))
    if (els.length) out.changed.push({ id, recordCode: r.recordCode, entityName: r.entityName, elements: els })
    else out.unchanged++
  }
  for (const [id, r] of a) if (!b.has(id)) out.removed.push({ id, recordCode: r.recordCode, entityName: r.entityName })
  return out
}

/** Records whose reported values differ from a stored fingerprint set — the "changed since" count. */
export function changedSince(hashes: Record<string, string>, records: StateRecord[]): { changed: StateRecord[]; added: StateRecord[]; removedIds: string[] } {
  const now = new Map(records.map((r) => [recordId(r), r]))
  const changed: StateRecord[] = [], added: StateRecord[] = []
  for (const [id, r] of now) { const h = hashes[id]; if (h == null) added.push(r); else if (h !== recordHash(r)) changed.push(r) }
  return { changed, added, removedIds: Object.keys(hashes).filter((id) => !now.has(id)) }
}

// ─────────────────────────────────────────────────────────────────────────────
// Collections in time
// ─────────────────────────────────────────────────────────────────────────────

export type CollectionPhase = 'upcoming' | 'open' | 'correction_window' | 'closed'
export function collectionPhase(c: { openAt: string; closeAt: string; correctionOpenAt?: string | null; correctionCloseAt?: string | null }, now: string): CollectionPhase {
  const d = now.slice(0, 10)
  if (d < c.openAt) return 'upcoming'
  if (d <= c.closeAt) return 'open'
  if (c.correctionOpenAt && c.correctionCloseAt && d >= c.correctionOpenAt && d <= c.correctionCloseAt) return 'correction_window'
  return 'closed'
}
export const daysUntil = (date: string, now: string) => Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${now.slice(0, 10)}T00:00:00Z`)) / 86_400_000)

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

const PROVIDERS = new Map<string, IStateReportingProvider>()
export const StateReportingProviderRegistry = {
  register(p: IStateReportingProvider) { PROVIDERS.set(p.providerCode, p) },
  get(code: string) { return PROVIDERS.get(code) ?? null },
  /** Only providers that are really implemented are offered; test providers never are. */
  offered() { return [...PROVIDERS.values()].filter((p) => p.status === 'active') },
  all() { return [...PROVIDERS.values()] },
}
